import {
  Directive,
  HostListener,
  ComponentRef,
  ViewContainerRef,
  ComponentFactoryResolver,
  Input,
  OnChanges,
  SimpleChange,
  Output,
  EventEmitter
} from '@angular/core';
import { PopoverComponent } from './popover.component';

@Directive({
  selector: '[appPopover]'
})
export class PopoverDirective implements OnChanges {
  protected popoverComponent = PopoverComponent;
  protected popover: ComponentRef<PopoverComponent>;
  public visible: boolean;

  constructor(protected viewContainerRef: ViewContainerRef, protected resolver: ComponentFactoryResolver) {}

  // tslint:disable-next-line:no-input-rename
  @Input('appPopover')
  public content: string | PopoverComponent;

  @Input()
  public popoverDisabled: boolean;

  @Input()
  public popoverAnimation: boolean;

  @Input()
  public popoverPlacement:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'auto'
    | 'auto top'
    | 'auto bottom'
    | 'auto left'
    | 'auto right';

  @Input()
  public popoverTitle: string;

  @Input()
  public popoverOnHover = false;

  @Input()
  public popoverCloseOnClickOutside: boolean;

  @Input()
  public popoverCloseOnMouseOutside: boolean;

  @Input()
  public popoverCloseOnResize = true;

  @Input()
  public popoverDismissTimeout = 0;

  @Output()
  public onShown = new EventEmitter<PopoverDirective>();

  @Output()
  public onHidden = new EventEmitter<PopoverDirective>();

  @HostListener('click')
  showOrHideOnClick(): void {
    if (this.popoverOnHover || this.popoverDisabled) {
      return;
    }
    this.toggle();
  }

  @HostListener('focusin')
  @HostListener('mouseenter')
  showOnHover(): void {
    if (!this.popoverOnHover || this.popoverDisabled) {
      return;
    }
    this.show();
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  hideOnHover(): void {
    // this.popoverCloseOnMouseOutside -> don't do anything since not we control this
    if (this.popoverCloseOnMouseOutside || !this.popoverOnHover || this.popoverDisabled) {
      return;
    }
    this.hide();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (!this.popoverCloseOnResize || this.popoverDisabled) {
      return;
    }
    this.hide();
  }

  ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    if (changes['popoverDisabled']) {
      if (changes['popoverDisabled'].currentValue) {
        this.hide();
      }
    }
  }

  public toggle() {
    if (!this.visible) {
      this.show();
    } else {
      this.hide(true);
    }
  }

  public show() {
    if (this.visible) {
      return;
    }
    this.visible = true;

    if (typeof this.content === 'string') {
      const factory = this.resolver.resolveComponentFactory(this.popoverComponent);
      if (!this.visible) {
        return;
      }

      this.popover = this.viewContainerRef.createComponent(factory);
      const popover = this.popover.instance as PopoverComponent;
      popover.popover = this;
      popover.content = this.content as string;
      if (this.popoverPlacement !== undefined) {
        popover.placement = this.popoverPlacement;
      }
      if (this.popoverAnimation !== undefined) {
        popover.animation = this.popoverAnimation;
      }
      if (this.popoverTitle !== undefined) {
        popover.title = this.popoverTitle;
      }
      if (this.popoverCloseOnClickOutside !== undefined) {
        popover.closeOnClickOutside = this.popoverCloseOnClickOutside;
      }
      if (this.popoverCloseOnMouseOutside !== undefined) {
        popover.closeOnMouseOutside = this.popoverCloseOnMouseOutside;
      }

      // if dismissTimeout option is set, then this popover will be dismissed in dismissTimeout time
      if (this.popoverDismissTimeout > 0) {
        setTimeout(() => this.hide(), this.popoverDismissTimeout);
      }
    } else {
      const popover = this.content as PopoverComponent;
      popover.popover = this;
      if (this.popoverPlacement !== undefined) {
        popover.placement = this.popoverPlacement;
      }
      if (this.popoverAnimation !== undefined) {
        popover.animation = this.popoverAnimation;
      }
      if (this.popoverTitle !== undefined) {
        popover.title = this.popoverTitle;
      }
      if (this.popoverCloseOnClickOutside !== undefined) {
        popover.closeOnClickOutside = this.popoverCloseOnClickOutside;
      }
      if (this.popoverCloseOnMouseOutside !== undefined) {
        popover.closeOnMouseOutside = this.popoverCloseOnMouseOutside;
      }

      // if dismissTimeout option is set, then this popover will be dismissed in dismissTimeout time
      if (this.popoverDismissTimeout > 0) {
        setTimeout(() => this.hide(), this.popoverDismissTimeout);
      }

      popover.show();
    }

    this.onShown.emit(this);
  }

  public hide(fromToggle = false) {
    if (!this.visible) {
      return;
    }

    this.visible = false;
    if (this.popover) {
      this.popover.destroy();
    }

    if (fromToggle && this.content instanceof PopoverComponent) {
      (this.content as PopoverComponent).hideFromPopover();
    }

    this.onHidden.emit(this);
  }

  public getElement() {
    return this.viewContainerRef.element.nativeElement;
  }
}
